import Decimal from 'decimal.js';
import type { DataProp } from 'editorjs-blocks-react-renderer';
import { Base64 } from 'js-base64';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import toNumber from 'lodash/toNumber';
import trimEnd from 'lodash/trimEnd';
import trimStart from 'lodash/trimStart';
import type { ParsedUrlQueryInput } from 'querystring';
import { parse, stringify } from 'querystring';

import type { Maybe } from '@/lib/types';
import type {
  PaymentGatewayConfig,
  PaymentMethodConfiguration,
} from '@/states/paymentGatewaysState';
import { checkCountryBillingSameShippingAddressAffirm } from '@/views/PaymentView/components/PaymentMethods/utils';

import { WORLD_WIDE_CATALOG_COUNTRY } from './const';
import { checkIsOneyPaymentByType } from './payment/paymentTypeChecks';
import type { PaymentType } from './payment/types';
import { PAYMENT_TYPES } from './payment/types';
import { getRegionFromLocale } from './regions';

const WATCH_SKU_REGEX = /^[A-Za-z][A-Za-z0-9]{11}[0-9]$/;

export const isWatchSku = (sku: string) => {
  return WATCH_SKU_REGEX.test(sku);
};

const WATCH_SKU_FIRST_SIX_REGEX = /^[A-Za-z][A-Za-z0-9]{5}$/;

export const isWatchSkuFirstSix = (sku: string) => {
  return WATCH_SKU_FIRST_SIX_REGEX.test(sku);
};

// Returns true for non nullable values
export function notNullable<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

export const cleanObject = <T extends Record<any, any>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => notNullable(value)),
  ) as T;

export const parseEditorJSData = (jsonStringData?: string): DataProp | null => {
  if (!jsonStringData) {
    return null;
  }
  let data;
  try {
    data = JSON.parse(jsonStringData);
  } catch (e) {
    return null;
  }

  if (!data.blocks?.length) {
    // No data to render
    return null;
  }

  // Path for compatibility with data from older version od EditorJS
  if (!data.time) {
    data.time = Date.now().toString();
  }
  if (!data.version) {
    data.version = '2.22.2';
  }

  return data;
};

export const makeFirstLetterUppercase = (text: Maybe<string>) => {
  if (text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return '';
};

/**
 * Saleor do not preserve order when querying for data by ids.
 */
interface ObjWithId {
  id: string;
}
export const getSortedObjects = <Obj extends ObjWithId>(
  idsInOrder: string[],
  objects: Obj[],
) =>
  objects.sort((a, b) => {
    const aIndex = idsInOrder.indexOf(a.id);
    const bIndex = idsInOrder.indexOf(b.id);

    if (aIndex > bIndex) {
      return 1;
    }

    if (aIndex < bIndex) {
      return -1;
    }

    return 0;
  });

export const getCurrentUrl = () => window.location.href.split('?')[0];

export const getOrigin = () => window.location.origin;

export const combinUrl = (base: string, relative: string) => {
  if (~relative.indexOf('://')) {
    return relative;
  }

  return `${trimEnd(base, '/')}/${trimStart(relative, '/')}`;
};

export const queryStringEncrypted = (query: ParsedUrlQueryInput) => {
  return Base64.encode(stringify(query));
};
export const queryStringDecrypted = (query: string) => {
  try {
    return parse(Base64.decode(query));
  } catch (error) {
    return null;
  }
};

export const convertToCents = (amount: number) => {
  return new Decimal(amount).mul(100).toNumber();
};

export const getAmountLimit = (
  type: Maybe<PaymentType>,
  gatewayConfigs: PaymentGatewayConfig[],
) => {
  const dropInConfiguration =
    gatewayConfigs
      .flatMap(({ paymentMethods }) => paymentMethods)
      .find((item) => item.type === type)?.dropInConfiguration || {};

  let limitMax;
  if (isNumber(dropInConfiguration.limitMax))
    limitMax = dropInConfiguration.limitMax;
  let limitMin;
  if (isNumber(dropInConfiguration.limitMin))
    limitMin = dropInConfiguration.limitMin;

  return { limitMax, limitMin };
};

export const filterAvailablePaymentMethods = (
  paymentMethods: PaymentMethodConfiguration[],
  hasWatch: boolean,
  minNextTransactionAmount?: number,
  maxNextTransactionAmount?: number,
  isPreOrder?: boolean,
  isDepositPaid?: boolean,
  singlePaymentMethod?: boolean,
  locale?: string,
  billingCountryCode?: string,
) => {
  const filterPaymentMethods = paymentMethods.filter(
    ({ dropInConfiguration, type }) => {
      if (isNil(minNextTransactionAmount) || isNil(maxNextTransactionAmount))
        return false;
      const limitMax = dropInConfiguration?.limitMax;
      const limitMin = dropInConfiguration?.limitMin;
      const formatLimitMax = toNumber(limitMax);
      const isUnderMaxLimit =
        isNil(limitMax) ||
        (isNumber(formatLimitMax) && formatLimitMax > minNextTransactionAmount);
      const formatLimitMin = toNumber(limitMin);
      const isAboveMinLimit =
        isNil(limitMin) ||
        (isNumber(formatLimitMin) &&
          maxNextTransactionAmount >= formatLimitMin);
      // BEC-24955: Klarna is watch-only; pre-order phases are handled below.
      if (type === PAYMENT_TYPES.KLARNA_ACCOUNT && !hasWatch) {
        return false;
      }
      if (
        type == 'affirm' ||
        type === PAYMENT_TYPES.KLARNA_ACCOUNT ||
        (checkIsOneyPaymentByType(type) && hasWatch)
      ) {
        return (!isPreOrder || isDepositPaid) && isUnderMaxLimit;
      } else {
        return isUnderMaxLimit && isAboveMinLimit;
      }
    },
  );

  if (!singlePaymentMethod) return filterPaymentMethods;
  // When there are multiple oney installment payment methods, only the first one is displayed.
  return filterPaymentMethods.reduce((arr, current) => {
    const isAffirmPaymentAvailable =
      checkCountryBillingSameShippingAddressAffirm(
        billingCountryCode,
        locale,
        current.type,
      );
    if (!isAffirmPaymentAvailable) return arr;

    const isOnePayment = checkIsOneyPaymentByType(current.type);
    const hasOnePayment = arr.find((item) =>
      checkIsOneyPaymentByType(item.type),
    );
    if (!isOnePayment || !hasOnePayment) {
      arr.push(current);
    }
    return arr;
  }, [] as PaymentMethodConfiguration[]);
};

export const hasFinancingPaymentMethod = (
  gatewayConfigs: PaymentGatewayConfig[],
  hasWatch: boolean,
  minNextTransactionAmount?: number,
  maxNextTransactionAmount?: number,
  isPreOrder?: boolean,
  isDepositPaid?: boolean,
  locale?: string,
  billingCountryCode?: string,
) => {
  return gatewayConfigs.some(({ paymentMethods }) => {
    const availablePaymentMethodTypes = filterAvailablePaymentMethods(
      paymentMethods,
      hasWatch,
      minNextTransactionAmount,
      maxNextTransactionAmount,
      isPreOrder,
      isDepositPaid,
      true,
      locale,
      billingCountryCode,
    ).map((paymentMethod) => paymentMethod.type);

    const financingPaymentMethods: PaymentType[] = [
      PAYMENT_TYPES.AFFIRM,
      PAYMENT_TYPES.AGOS,
      PAYMENT_TYPES.CEMBRA,
      PAYMENT_TYPES.CEMBRA_INVOICE,
      PAYMENT_TYPES.CREDIT_PLUS,
      PAYMENT_TYPES.FACILYPAY_3X, // Oney
      PAYMENT_TYPES.FACILYPAY_4X, // Oney
      PAYMENT_TYPES.KLARNA_ACCOUNT, // Klarna Pay in 3
      PAYMENT_TYPES.V12,
      PAYMENT_TYPES.ORICO_FINANCING,
      PAYMENT_TYPES.ZIP,
    ];

    return availablePaymentMethodTypes.some((type) =>
      financingPaymentMethods.includes(type),
    );
  });
};

export const containsPattern = (text: string, pattern?: string | null) => {
  if (!pattern) {
    return false;
  }
  const regex = new RegExp(pattern);
  return regex.test(text);
};

export const isWorldWideCatalogCountry = (locale?: string | undefined) => {
  const { country } = getRegionFromLocale(locale);
  return WORLD_WIDE_CATALOG_COUNTRY?.includes(country.code);
};

// https://breitling.atlassian.net/browse/BEC-15214?focusedCommentId=629283
export const formatProductSlug = (slug: string) =>
  slug.replaceAll('.', '_').replaceAll('/', '-');

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function nativeFetchWithRetry(
  url: string,
  options?: RequestInit,
  retries = 3,
  delay = 500,
): Promise<Response> {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    // For network errors, retry if attempts remaining
    if (retries > 0) {
      await wait(delay);
      return nativeFetchWithRetry(url, options, retries - 1, delay);
    }
    throw error;
  }
}
